import { gzipSync, strToU8 } from "@-/com.npmjs.fflate";
import * as z from "@-/com.npmjs.zod";
import { captureSentryDiagnosticReport } from "@-/dev.improve.haniverse.lib.sentry";
import { resolveHanWorkerUrl } from "@-/dev.improve.haniverse.lib.worker";
import { buildDiagnosticsReport } from "#build-diagnostics-report";
import { markDiagnostics } from "#diagnostics-recorder";
import type { DiagnosticsReport } from "#diagnostics-types";

// Shape returned by the worker's `POST /diagnostic` route (apps/haniverse-worker).
const diagnosticUploadResponseSchema = z.object({
  id: z.string().min(1),
  reportUrl: z.string().min(1),
});

const TRAILING_SLASHES_RE = /\/+$/;

export type SendDiagnosticsReportResult = {
  compressedByteLength: number;
  rawByteLength: number;
  /** Worker-assigned id, or `null` when the upload failed but the Sentry event went out. */
  reportId: string | null;
  reportUrl: string | null;
  /** Sentry event id, or `null` when Sentry is disabled (dev builds) or the capture failed. */
  sentryEventId: string | null;
};

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function summarize(report: DiagnosticsReport): Record<string, unknown> {
  const samples = report.session.samples;
  const latest = samples.at(-1);
  const cpuValues = samples
    .map((sample) => sample.process?.cpuPercent ?? null)
    .filter((value): value is number => value !== null);
  const footprints = samples
    .map((sample) => sample.process?.memoryFootprintBytes ?? null)
    .filter((value): value is number => value !== null);
  const events = report.session.events;
  return {
    cpuPercentMax: cpuValues.length > 0 ? Math.max(...cpuValues) : null,
    errorCount: events.filter((event) => event.kind === "error").length,
    jsStallCount: events.filter((event) => event.kind === "js-stall").length,
    logCount: report.session.logs.length,
    longTaskCount: events.filter((event) => event.kind === "long-task").length,
    memoryFootprintLatestBytes: latest?.process?.memoryFootprintBytes ?? null,
    memoryFootprintMaxBytes: footprints.length > 0 ? Math.max(...footprints) : null,
    memoryWarningCount: events.filter((event) => event.kind === "memory-warning").length,
    route: report.session.currentRoute,
    sampleCount: samples.length,
    slowestInteractionMs: Math.max(
      0,
      ...events.flatMap((event) => (event.kind === "interaction" ? [event.durationMs] : [])),
    ),
    uptimeMs: report.session.uptimeMs,
  };
}

async function uploadToWorker(
  compressed: Uint8Array<ArrayBuffer>,
): Promise<{ reportId: string; reportUrl: string }> {
  const workerBaseUrl = resolveHanWorkerUrl().replace(TRAILING_SLASHES_RE, "");
  const response = await fetch(`${workerBaseUrl}/diagnostic`, {
    body: compressed,
    // gzip as the CONTENT TYPE, not `Content-Encoding`: intermediaries may transparently inflate
    // an encoded request body, and the worker stores the bytes it receives verbatim.
    headers: { "Content-Type": "application/gzip" },
    method: "POST",
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Upload failed (${response.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`,
    );
  }
  const parsed = diagnosticUploadResponseSchema.parse(await response.json());
  return { reportId: parsed.id, reportUrl: parsed.reportUrl };
}

/**
 * Builds the report, uploads it (gzipped) to the haniverse-worker, and files a Sentry event that
 * links to it with the report attached. Either leg alone counts as success — the worker may be
 * unreachable offline, and Sentry is disabled in dev builds — but if BOTH fail this throws with
 * the upload error so the UI can offer the share-sheet fallback.
 */
export async function sendDiagnosticsReport(
  options: { userNote?: string | null } = {},
): Promise<SendDiagnosticsReportResult> {
  markDiagnostics("send-diagnostic-report");
  const report = await buildDiagnosticsReport({ userNote: options.userNote ?? null });
  const raw = strToU8(JSON.stringify(report));
  const compressed = gzipSync(raw, { level: 6 });

  let reportId: string | null = null;
  let reportUrl: string | null = null;
  let uploadError: unknown;
  try {
    ({ reportId, reportUrl } = await uploadToWorker(compressed));
  } catch (error) {
    uploadError = error;
  }

  const timestamp = report.generatedAt.replaceAll(/[:.]/g, "-");
  let sentryEventId: string | null = null;
  let sentryError: unknown;
  try {
    sentryEventId = await captureSentryDiagnosticReport({
      attachment: {
        contentType: "application/gzip",
        data: compressed,
        filename: `han-diagnostic-${timestamp}.json.gz`,
      },
      extra: {
        note: report.userNote,
        reportUrl,
        summary: summarize(report),
        uploadError: uploadError === undefined ? null : describeError(uploadError),
      },
      message: `Diagnostic report ${reportId ?? "(upload failed)"}`,
      tags: {
        "diagnostics.report": "true",
        "diagnostics.report_id": reportId ?? "none",
        "diagnostics.route": report.session.currentRoute ?? "unknown",
      },
    });
  } catch (error) {
    sentryError = error;
  }

  if (reportUrl === null && sentryEventId === null) {
    const reasons = [uploadError, sentryError]
      .filter((error) => error !== undefined)
      .map((error) => describeError(error));
    throw new Error(
      `Could not send the diagnostic report${reasons.length > 0 ? `: ${reasons.join("; ")}` : ""}`,
    );
  }

  return {
    compressedByteLength: compressed.byteLength,
    rawByteLength: raw.byteLength,
    reportId,
    reportUrl,
    sentryEventId,
  };
}
